/**
 * Session: access token in memory, refresh token in localStorage (the
 * cms-front trade-off — a parent stays signed in across restarts). Three
 * OTP surfaces of Auth: family (guardian), person (the autistic person
 * themself) and specialist (therapist). Local test sign-in via the dev JWKS.
 */

import { runtimeConfig } from './config';

const REFRESH_KEY = 'zs.refreshToken';
const SURFACE_KEY = 'zs.surface';
/** LOCAL ONLY: the dev JWKS issues no refresh token, so its access token is kept across reloads. */
const DEV_ACCESS_KEY = 'zs.devAccess';

export type Surface = 'family' | 'person' | 'specialist';

export interface TokenSet {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
}

let accessToken: string | null = null;
let accessExpiresAt = 0;
try {
  const dev = localStorage.getItem(DEV_ACCESS_KEY);
  if (dev) {
    const parsed = JSON.parse(dev) as { token: string; exp: number };
    if (parsed.exp > Date.now()) {
      accessToken = parsed.token;
      accessExpiresAt = parsed.exp;
    } else localStorage.removeItem(DEV_ACCESS_KEY);
  }
} catch {
  /* ignore */
}
let refreshing: Promise<string> | null = null;
const listeners = new Set<() => void>();

export function onSessionChange(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}
const notify = () => listeners.forEach((l) => l());

export const getAccessToken = () => accessToken;
export function getStoredRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}
export function getSurface(): Surface {
  try {
    const s = localStorage.getItem(SURFACE_KEY);
    return s === 'person' || s === 'specialist' ? s : 'family';
  } catch {
    return 'family';
  }
}

export function storeTokenSet(t: TokenSet, surface: Surface): void {
  accessToken = t.access_token;
  accessExpiresAt = typeof t.expires_in === 'number' ? Date.now() + t.expires_in * 1000 : 0;
  try {
    if (t.refresh_token) localStorage.setItem(REFRESH_KEY, t.refresh_token);
    localStorage.setItem(SURFACE_KEY, surface);
  } catch {
    /* memory-only session */
  }
  notify();
}

export function clearSession(): void {
  accessToken = null;
  accessExpiresAt = 0;
  refreshing = null;
  try {
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(DEV_ACCESS_KEY);
  } catch {
    /* ignore */
  }
  notify();
}

export function hasSession(): boolean {
  return accessToken !== null || getStoredRefreshToken() !== null;
}

function stale(): boolean {
  if (accessToken === null) return true;
  if (accessExpiresAt === 0) return false;
  return Date.now() > accessExpiresAt - 30_000;
}

export async function refreshAccessToken(): Promise<string> {
  if (refreshing) return refreshing;
  const refresh = getStoredRefreshToken();
  if (refresh === null) {
    clearSession();
    throw new Error('no refresh token');
  }
  refreshing = (async () => {
    try {
      const res = await fetch(`${runtimeConfig().authApiUrl}/auth/token/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!res.ok) {
        if (res.status === 401) clearSession();
        throw new Error(`refresh failed: ${res.status}`);
      }
      const t = (await res.json()) as TokenSet;
      storeTokenSet(t, getSurface());
      return t.access_token;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function freshAccessToken(): Promise<string | null> {
  if (!stale()) return accessToken;
  if (getStoredRefreshToken() === null) return accessToken;
  try {
    return await refreshAccessToken();
  } catch {
    return accessToken;
  }
}

/* ---------- Auth OTP surfaces ---------- */

export interface OtpAck {
  message: string;
  expiresInSeconds: number;
  resendCooldownSeconds: number;
  /** Development/staging only: Auth returns the code so testers never need SMS. */
  devCode?: string;
}

async function authPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${runtimeConfig().authApiUrl}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const parsed = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new Error(parsed?.message ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export const otpRequest = (surface: Surface, phone: string) =>
  authPost<OtpAck>(`/auth/${surface}/otp/request`, { phone });

export async function otpVerify(surface: Surface, phone: string, code: string): Promise<void> {
  const t = await authPost<TokenSet>(`/auth/${surface}/otp/verify`, { phone, code });
  storeTokenSet(t, surface);
}

export async function logout(): Promise<void> {
  const refresh = getStoredRefreshToken();
  if (refresh) {
    try {
      await authPost('/auth/logout', { refresh_token: refresh });
    } catch {
      /* best effort */
    }
  }
  clearSession();
}

/** LOCAL ONLY: fetch a signed token for a demo actor from scripts/dev/jwks-dev.mjs. */
export async function devSignIn(name: string): Promise<void> {
  const base = runtimeConfig().devTokenUrl;
  if (!base) throw new Error('dev tokens are not configured');
  const res = await fetch(`${base}/token/${name}`);
  if (!res.ok) throw new Error('dev token server is not running');
  const t = (await res.json()) as TokenSet & { apc: Surface };
  storeTokenSet(t, t.apc);
  try {
    localStorage.setItem(
      DEV_ACCESS_KEY,
      JSON.stringify({ token: t.access_token, exp: Date.now() + (t.expires_in ?? 3600) * 1000 }),
    );
  } catch {
    /* ignore */
  }
}
